import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axios from 'axios';

// Define interfaces for the API response
export interface E09DispatchData {
  sampleDbId: number;
  dataSetId: number;
  version: number;
  changeBy: string;
  comments: string;
  sampleStatus: string | null;
  exchangeOfficeByInboundOe: string;
  exchangeOfficeByOutboundOe: string;
  delFlag: number;
  despatchDate: string;
  despatchMailSubclass: string;
  despatchNumber: number;
  mailCategory: string;
  oeFromCode: string | null;
  oeToCode: string | null;
  receivingPpo: string | null;
  sendingPpo: string | null;
  validFlag: number;
  barcode: string;
  effectiveSamplingRateStratum: string | null;
  formatAnnounced: string | null;
  formatCorrected: string;
  grossKg: number;
  ibrsKg: number;
  labelWeight: number;
  netKg: number;
  netOrGrossAccounting: string;
  receivedReceptaclesStratum: string | null;
  receivedWeightStratum: string | null;
  receptacleCategory: string;
  receptacleMailSubclass: string;
  receptacleNumber: number;
  sampledWeightStratum: string | null;
  receptacleType: string;
  samplingDate: string | null;
  samplingMethod: string;
  samplingRate: number;
  stratum: string | null;
  subsampleRate: number;
  tareKg: number;
  sampleId: string | null;
  rplusV: number;
  pkg: number;
  gkg: number;
  ekg: number;
  pitems: number;
  gitems: number;
  eitems: number;
  fbag: number;
  ckg: number | null;
  citems: number | null;
}

export interface E09DispatchDataResponse {
  content: E09DispatchData[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  last: boolean;
  totalElements: number;
  totalPages: number;
  first: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  empty: boolean;
}

export interface SortConfig {
  column: string;
  direction: 'ASC' | 'DESC';
}

export interface FilterConfig {
  column: string;
  term: string;
}

interface UseE09DispatchDataParams {
  e09FileId: number;
  page?: number;
  size?: number;
  sorts?: SortConfig[];
  filters?: FilterConfig[];
  sort?: string; // Deprecated: use sorts instead
  direction?: 'ASC' | 'DESC'; // Deprecated: use sorts instead
  enabled?: boolean;
  searchColumn?: string; // Deprecated: use filters instead
  searchTerm?: string; // Deprecated: use filters instead
  status?: string;
}

/**
 * Hook to fetch e09 dispatch data for a specific file
 * 
 * @param params Parameters including fileId, pagination options, sorting options, and query enabling flag
 * @returns Query result with e09 dispatch data
 */
export const useE09DispatchData = ({ 
  e09FileId, 
  page = 0, 
  size = 200,
  sorts,
  filters,
  sort, // Legacy support
  direction, // Legacy support
  enabled = true,
  searchColumn, // Legacy support
  searchTerm, // Legacy support
  status
}: UseE09DispatchDataParams) => {
  // Combine filters array with legacy searchColumn/searchTerm for backward compatibility
  const allFilters = useMemo(() => {
    const filterList: FilterConfig[] = filters ? [...filters] : [];
    // Add legacy searchColumn/searchTerm if provided and not already in filters
    if (searchColumn && searchTerm && !filterList.some(f => f.column === searchColumn)) {
      filterList.push({ column: searchColumn, term: searchTerm });
    }
    return filterList;
  }, [filters, searchColumn, searchTerm]);

  // Combine sorts array with legacy sort/direction for backward compatibility
  const allSorts = useMemo(() => {
    const sortList: SortConfig[] = sorts ? [...sorts] : [];
    // Add legacy sort/direction if provided and not already in sorts
    if (sort && direction && !sortList.some(s => s.column === sort)) {
      sortList.push({ column: sort, direction });
    }
    return sortList;
  }, [sorts, sort, direction]);

  return useQuery<E09DispatchDataResponse>({
    queryKey: ['e09DispatchData', e09FileId, page, size, allSorts, allFilters, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('e09FileId', String(e09FileId));
      params.append('page', String(page));
      params.append('size', String(size));
      
      // Add multiple sorting parameters if provided
      if (allSorts && allSorts.length > 0) {
        allSorts.forEach((sort) => {
          // Format for backend sorting: "fieldName,direction"
          params.append('sort', `${sort.column},${sort.direction}`);
        });
      }

      // Add multiple filter parameters if provided
      if (allFilters && allFilters.length > 0) {
        allFilters.forEach((filter) => {
          if (filter.column && filter.term) {
            params.append(filter.column, filter.term);
          }
        });
      }

      // Add status parameter if provided
      if (status && status !== 'all') {
        params.append('status', status);
      }

      const response = await axios.get(`/data-sample/e09?${params.toString()}`);
      return response.data;
    },
    enabled: enabled && !!e09FileId,
    staleTime: 0, // Set to 0 to ensure fresh data on pagination changes
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes, but will always refetch
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });
}; 